/** How many photos one player may keep on one position, and on the meeting itself. */
export const HANDSHAKE_PHOTO_LIMIT = 5;

/** How many of these files still fit, once the ones already there are counted. */
export function photosToUpload(files: FileList | File[] | null | undefined, already: number): File[] {
  const room = Math.max(0, HANDSHAKE_PHOTO_LIMIT - already);

  return Array.from(files ?? []).slice(0, room);
}

export type HandshakePhotoRef = {
  imageBlobHash: string;
  userId: string | null;
  positionIndex: number | null;
};

type HandshakePhotos = {
  photos?: HandshakePhotoRef[];
  initiatorId?: string | null;
  responderId?: string | null;
  initiatorImageBlobHash?: string | null;
  responderImageBlobHash?: string | null;
};

/** One card in a handshake's row: a played position with its photos above it, or the meeting photos alone. */
export interface PhotoCard {
  key: string;
  /** Null for the meeting, which has no rank under it. */
  position: number | null;
  /** Newest first. The first one is the photo on top. */
  hashes: string[];
}

/**
 * The row of cards, in the order the positions are given. A photo whose
 * position is no longer played joins the meeting, which comes last.
 */
export function photoCards(
  positions: number[],
  photos: { hash: string; positionIndex: number | null; createdAt?: string | null }[],
): PhotoCard[] {
  const newestFirst = photos
    .map((photo, order) => ({ ...photo, order }))
    .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? '') || left.order - right.order);
  const played = new Set(positions);

  const cards: PhotoCard[] = positions.map((position) => ({
    key: `position-${position}`,
    position,
    hashes: newestFirst.filter((photo) => photo.positionIndex === position).map((photo) => photo.hash),
  }));

  const meeting = newestFirst
    .filter((photo) => photo.positionIndex == null || !played.has(photo.positionIndex))
    .map((photo) => photo.hash);
  if (meeting.length) cards.push({ key: 'meeting', position: null, hashes: meeting });

  return cards;
}

/** Positions where this owner already has five other photos, so this one cannot join them. */
export function positionsAtPhotoLimit(
  photos: HandshakePhotoRef[],
  ownerId: string | null,
  exceptHash: string,
): number[] {
  const counts = new Map<number, number>();

  for (const photo of photos) {
    if (photo.userId !== ownerId || photo.imageBlobHash === exceptHash || photo.positionIndex == null) continue;

    counts.set(photo.positionIndex, (counts.get(photo.positionIndex) ?? 0) + 1);
  }

  return [...counts.entries()].filter(([, count]) => count >= HANDSHAKE_PHOTO_LIMIT).map(([position]) => position);
}

/**
 * Photos this player already has in one place. A meeting photo still stored
 * only on the handshake, with no row, counts as one of the five.
 */
export function photosInSlot(
  handshake: HandshakePhotos,
  ownerId: string | null,
  positionIndex: number | null,
  exceptHash?: string,
): number {
  const photos = (handshake.photos ?? []).filter((photo) => photo.imageBlobHash !== exceptHash);
  const rows = photos.filter(
    (photo) =>
      photo.userId === ownerId &&
      (positionIndex == null ? photo.positionIndex == null : photo.positionIndex === positionIndex),
  ).length;

  if (positionIndex != null || !ownerId) return rows;

  const column =
    ownerId === handshake.initiatorId
      ? handshake.initiatorImageBlobHash
      : ownerId === handshake.responderId
        ? handshake.responderImageBlobHash
        : null;

  if (column && column !== exceptHash && !photos.some((photo) => photo.imageBlobHash === column)) return rows + 1;

  return rows;
}
